import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MapPin } from 'lucide-react';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useLocations } from '@/features/locations/api';
import { useConvertQuote, useDeleteQuote, useDuplicateQuote, useQuote, useSendQuote, useVoidQuote } from './api';
import { SendQuoteCard } from './SendQuoteCard';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useLocaleFormatters } from '@/lib/format';

// §34.2 - minutes-from-midnight -> "Jul 12, 2026 9:00 AM" using the same
// locale-aware formatters as the rest of the page (no raw Intl calls here,
// per CLAUDE.md).
function formatPreferredBooking(
  preferredDate: string, preferredTime: number | null,
  formatDate: (date: Date, dateFormat: string) => string,
  formatTimeOfDay: (minutes: number) => string,
): string {
  const dateLabel = formatDate(new Date(`${preferredDate}T00:00:00`), 'MMM d, yyyy');
  return preferredTime === null ? dateLabel : `${dateLabel} · ${formatTimeOfDay(preferredTime)}`;
}

export function QuoteDetailPage() {
  const { t } = useTranslation('quotes');
  const { formatMoney, formatDateTime, formatDate, formatTimeOfDay } = useLocaleFormatters();
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const isManager = membership?.role === 'owner' || membership?.role === 'manager';
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const { data: quote } = useQuote(workspaceId, quoteId);
  const { data: locationsData } = useLocations(workspaceId);
  const voidQuote = useVoidQuote(workspaceId, quoteId ?? '');
  const duplicateQuote = useDuplicateQuote(workspaceId, quoteId ?? '');
  const convertQuote = useConvertQuote(workspaceId, quoteId ?? '');
  const deleteQuote = useDeleteQuote(workspaceId, quoteId ?? '');
  const sendQuote = useSendQuote(workspaceId, quoteId ?? '');
  const { confirm, ConfirmDialog } = useConfirm();

  if (!quote) return null;

  const location = locationsData?.items.find((l) => l.id === quote.locationId);

  async function handleDelete() {
    const ok = await confirm({
      title: t('detail.confirmDelete.title'),
      description: t('detail.confirmDelete.description'),
      confirmLabel: t('common:actions.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    await deleteQuote.mutateAsync();
    toast.success(t('detail.toast.deleted'));
    navigate('/quotes');
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
      <ConfirmDialog />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold">Q-{quote.number}</h1>
        <span className="bg-surface-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-muted">
          {quote.status === 'deposit_paid' && quote.depositCents <= 0 ? t('accepted') : t(`status.quote.${quote.status}`)}
        </span>
        {gateOn && location ? (
          <span className="flex items-center gap-1 border border-border px-2 py-0.75 text-[11px] font-medium text-muted">
            <MapPin size={11} /> {location.name}
          </span>
        ) : null}
        <div className="flex flex-wrap gap-2 md:ml-auto">
          {quote.status === 'draft' || quote.status === 'sent' || quote.status === 'viewed' ? (
            <Button size="sm" variant="outline" onClick={() => navigate(`/quotes/new?quoteId=${quote.id}`)}>
              {t('common:actions.edit')}
            </Button>
          ) : null}
          {isManager && (quote.status === 'draft' || quote.status === 'sent' || quote.status === 'viewed') ? (
            <Button size="sm" variant="outline" onClick={() => voidQuote.mutate()}>
              {t('detail.actions.void')}
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => duplicateQuote.mutate()}>
            {t('detail.actions.duplicate')}
          </Button>
          {quote.status === 'draft' ? (
            <Button size="sm" variant="outline" className="text-danger" loading={deleteQuote.isPending} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          ) : null}
          {isManager && quote.status === 'sent' ? (
            <Button size="sm" onClick={async () => { await convertQuote.mutateAsync(); toast.success(t('detail.toast.convertedToJob')); }}>
              {t('detail.actions.convertToJob')}
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{quote.customerName} · {quote.vehicleLabel}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {quote.lineItems.map((li) => (
            <div key={li.id} className="flex items-center justify-between text-sm">
              <div>
                <div className="font-medium text-ink">{li.serviceName} · {li.coverageName}</div>
                <div className="text-xs text-muted">{li.materialBrand} {li.materialSeries}{li.color ? ` · ${li.color}` : ''}</div>
              </div>
              <div className="font-mono">{formatMoney(li.lineTotalCents)}</div>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-border pt-2 text-sm text-muted">
            <span>{t('detail.subtotal')}</span>
            <span className="font-mono">{formatMoney(quote.subtotalCents)}</span>
          </div>
          {quote.taxCents > 0 ? (
            <div className="flex items-center justify-between text-sm text-muted">
              <span>{t('detail.tax')}</span>
              <span className="font-mono">{formatMoney(quote.taxCents)}</span>
            </div>
          ) : null}
          <div className="flex items-center justify-between text-base font-bold">
            <span>{t('detail.total')}</span>
            <span className="font-mono">{formatMoney(quote.totalCents)}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-muted">
            <span>{t('detail.deposit', { pct: quote.depositPct })}</span>
            <span className="font-mono">{formatMoney(quote.depositCents)}</span>
          </div>
        </CardContent>
      </Card>

      {quote.status === 'draft' || quote.status === 'sent' || quote.status === 'viewed' || quote.status === 'expired' ? (
        <SendQuoteCard
          workspaceId={workspaceId}
          customerId={quote.customerId}
          vehicleId={quote.vehicleId}
          quoteId={quote.lineItems.length > 0 ? quote.id : null}
          sendQuote={sendQuote}
          totalCents={quote.totalCents}
          depositCents={quote.depositCents}
          shareUrl={quote.shareUrl}
          alreadySent={quote.status !== 'draft'}
        />
      ) : null}

      {quote.customerNotes ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.customerNotes')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">{quote.customerNotes}</CardContent>
        </Card>
      ) : null}

      {quote.customerRequestNote ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.customerRequest.title')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">{quote.customerRequestNote}</CardContent>
        </Card>
      ) : null}

      {quote.internalNotes ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.internalNotes')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">{quote.internalNotes}</CardContent>
        </Card>
      ) : null}

      {quote.preferredDate ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.preferredBooking.title')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">
            {formatPreferredBooking(quote.preferredDate, quote.preferredTime, formatDate, formatTimeOfDay)}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('detail.timeline.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <div>{t('detail.timeline.created', { date: formatDateTime(new Date(quote.createdAt)) })}</div>
          {quote.sentAt ? <div>{t('detail.timeline.sent', { date: formatDateTime(new Date(quote.sentAt)) })}</div> : null}
          {quote.viewedAt ? <div>{t('detail.timeline.viewed', { date: formatDateTime(new Date(quote.viewedAt)) })}</div> : null}
          {quote.depositPaidAt ? (
            <div>
              {quote.depositCents > 0
                ? t('detail.timeline.depositPaid', { date: formatDateTime(new Date(quote.depositPaidAt)) })
                : t('detail.timeline.accepted', { date: formatDateTime(new Date(quote.depositPaidAt)) })}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
